import { useEffect, useId, useState } from "react";
import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useDeletePreview,
  useDeleteUser,
  type AdminUser,
  type HardDeleteResponse,
} from "@/hooks/useAdminUsers";

interface Props {
  user: AdminUser | null;
  open: boolean;
  onClose: () => void;
  onDeleted?: (result: HardDeleteResponse) => void;
}

export function ConfirmDeleteUserModal({
  user,
  open,
  onClose,
  onDeleted,
}: Props) {
  const [confirmEmail, setConfirmEmail] = useState("");
  const emailInputId = useId();

  const preview = useDeletePreview(open && user ? user.id : null);
  const deleteUser = useDeleteUser();

  // Reset form when modal closes.
  useEffect(() => {
    if (!open) {
      setConfirmEmail("");
      deleteUser.reset();
    }
    // deleteUser is stable across renders from React Query; safe to exclude.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fire onDeleted callback on success.
  useEffect(() => {
    if (deleteUser.isSuccess && deleteUser.data && onDeleted) {
      onDeleted(deleteUser.data);
    }
  }, [deleteUser.isSuccess, deleteUser.data, onDeleted]);

  const emailMatches =
    !!user &&
    confirmEmail.trim().toLowerCase() === user.email.trim().toLowerCase();

  function handleDelete() {
    if (!user || !emailMatches) return;
    deleteUser.mutate({ userId: user.id, confirmEmail: confirmEmail.trim() });
  }

  const result = deleteUser.data;
  const showResult = deleteUser.isSuccess && !!result;
  const cleanupHadExternalFailure =
    showResult &&
    (result.clerk_ok === false || result.stripe_ok === false);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-destructive">
            Delete user permanently
          </DialogTitle>
          <DialogDescription>
            This action cannot be undone. Review the impact carefully before
            confirming.
          </DialogDescription>
        </DialogHeader>

        {!user ? null : showResult ? (
          <DeleteResultPanel
            result={result!}
            cleanupHadExternalFailure={cleanupHadExternalFailure}
            onClose={onClose}
          />
        ) : (
          <div className="space-y-4">
            <div className="text-sm">
              <div className="font-medium">{user.email}</div>
              <div className="text-muted-foreground text-xs font-mono">
                {user.id}
              </div>
            </div>

            {/* Preview counts */}
            {preview.isLoading && (
              <p className="text-sm text-muted-foreground">
                Loading impact preview…
              </p>
            )}
            {preview.error && (
              <p className="text-sm text-destructive">
                Failed to load preview:{" "}
                {preview.error instanceof Error
                  ? preview.error.message
                  : "unknown error"}
              </p>
            )}
            {preview.data && (
              <>
                <div className="space-y-1">
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Will be deleted
                  </div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 rounded-md border p-4 text-sm">
                    <CountRow
                      label="Concepts"
                      value={preview.data.concept_count}
                    />
                    <CountRow
                      label="Highlights"
                      value={preview.data.paper_highlight_count}
                    />
                    <CountRow
                      label="PDFs"
                      value={preview.data.paper_pdf_count}
                    />
                    <CountRow
                      label="AI Summaries"
                      value={preview.data.per_user_ai_summary_count}
                    />
                    <CountRow
                      label="R2 Blobs"
                      value={preview.data.r2_blobs_to_delete}
                    />
                    <BlobSizeRow
                      totalBytes={preview.data.r2_blobs_total_bytes}
                      unknownCount={preview.data.r2_blobs_unknown_size_count}
                      hasBlobs={preview.data.r2_blobs_to_delete > 0}
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Will be retained for analytics
                  </div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 rounded-md border p-4 text-sm">
                    <CountRow
                      label="Usage Events"
                      value={preview.data.retained_usage_event_count}
                    />
                    <CountRow
                      label="PDF Retrievals"
                      value={preview.data.retained_pdf_retrieval_event_count}
                    />
                    <CountRow
                      label="Feedback Events"
                      value={preview.data.retained_feedback_event_count}
                    />
                  </div>
                </div>

                <div className="space-y-1 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">
                      Stripe subscription
                    </span>
                    <span>
                      {preview.data.stripe_subscription_status === "active"
                        ? "Will be canceled immediately"
                        : preview.data.stripe_subscription_status === "canceled"
                          ? "Already canceled"
                          : "No subscription"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Clerk account</span>
                    <span>
                      {preview.data.clerk_user_exists
                        ? "Will be deleted"
                        : "Already gone"}
                    </span>
                  </div>
                </div>

                {preview.data.warnings.length > 0 && (
                  <div className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm">
                    <div className="flex items-center gap-2 font-medium text-amber-600 dark:text-amber-400">
                      <AlertTriangle className="size-4" />
                      Warnings
                    </div>
                    <ul className="mt-2 list-disc pl-6 space-y-1">
                      {preview.data.warnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}

            {/* Typed-email guard */}
            <div className="space-y-2">
              <Label htmlFor={emailInputId}>
                Type the user&apos;s email ({user.email}) to confirm
              </Label>
              <Input
                id={emailInputId}
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                placeholder={user.email}
                autoComplete="off"
              />
            </div>

            {deleteUser.isError && (
              <div className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
                {deleteUser.error instanceof Error
                  ? deleteUser.error.message
                  : "Delete failed."}
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={onClose}
                disabled={deleteUser.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={!emailMatches || deleteUser.isPending}
              >
                {deleteUser.isPending ? "Deleting…" : "Delete user"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CountRow({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono font-medium">{value}</span>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(
    units.length - 1,
    Math.floor(Math.log(bytes) / Math.log(1024)),
  );
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function BlobSizeRow({
  totalBytes,
  unknownCount,
  hasBlobs,
}: {
  totalBytes: number;
  unknownCount: number;
  hasBlobs: boolean;
}) {
  if (!hasBlobs) return null;
  return (
    <div className="col-span-2 flex items-center justify-between">
      <span className="text-muted-foreground">Total blob size</span>
      <span className="font-mono font-medium">
        {formatBytes(totalBytes)}
        {unknownCount > 0 && (
          <span className="ml-2 text-xs text-muted-foreground">
            ({unknownCount} of unknown size)
          </span>
        )}
      </span>
    </div>
  );
}

function DeleteResultPanel({
  result,
  cleanupHadExternalFailure,
  onClose,
}: {
  result: HardDeleteResponse;
  cleanupHadExternalFailure: boolean;
  onClose: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
        <CheckCircle2 className="size-5" />
        <span className="font-medium">User deleted</span>
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-2 rounded-md border p-4 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Tombstoned</span>
          <span>{result.tombstoned ? "Yes" : "No"}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Clerk</span>
          <ExternalStatus value={result.clerk_ok} />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Stripe</span>
          <ExternalStatus value={result.stripe_ok} />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">R2 blobs deleted</span>
          <span className="font-mono">{result.r2_blobs_deleted}</span>
        </div>
      </div>

      {result.r2_errors.length > 0 && (
        <div className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm">
          <div className="flex items-center gap-2 font-medium text-amber-600 dark:text-amber-400">
            <AlertTriangle className="size-4" />
            R2 errors ({result.r2_errors.length})
          </div>
          <ul className="mt-2 space-y-1">
            {result.r2_errors.map((e, i) => (
              <li key={i} className="font-mono text-xs">
                <span className="break-all">{e.key}</span>{" "}
                <span className="text-destructive">— {e.error}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {cleanupHadExternalFailure && (
        <div className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm">
          <div className="font-medium text-amber-600 dark:text-amber-400">
            External cleanup incomplete
          </div>
          <p className="mt-1 text-muted-foreground">
            Clerk and/or Stripe cleanup reported a failure. Use the
            &quot;Retry external cleanup&quot; admin endpoint to re-run just
            those external calls without re-deleting database state.
          </p>
        </div>
      )}

      <DialogFooter>
        <Button onClick={onClose}>Close</Button>
      </DialogFooter>
    </div>
  );
}

function ExternalStatus({ value }: { value: boolean | null }) {
  if (value === null)
    return <span className="text-muted-foreground">n/a</span>;
  if (value)
    return (
      <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
        <CheckCircle2 className="size-3.5" /> OK
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-destructive">
      <XCircle className="size-3.5" /> Failed
    </span>
  );
}

export default ConfirmDeleteUserModal;
